import React, { useState, useRef, useEffect, useCallback } from 'react';
import { apiFetch, API_BASE } from '../apiClient';
import VoiceInput from './VoiceInput';

// "Aufgabe anpassen" — chat-based editor for the task summary. Same UI/logic as
// ProcessEditChat (reuses its CSS classes), but it edits the summary and uses
// the relaxed clarification rule (the model proposes directly when clear).
export default function TaskEditChat({ summaryData, departmentName, onApplyChanges, onClose }) {
  const [editMessages, setEditMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pendingProposal, setPendingProposal] = useState(null);

  const voiceRef = useRef(null);
  const textareaRef = useRef(null);
  const chatEndRef = useRef(null);

  useEffect(() => { textareaRef.current?.focus(); }, []);
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [editMessages, pendingProposal]);

  const sendToEditChat = useCallback(async (allMessages) => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await apiFetch(`${API_BASE}/api/task-edit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: allMessages, summaryData, departmentName }),
      });

      const contentType = res.headers.get('content-type') || '';
      if (!res.ok || !contentType.includes('application/json')) {
        throw new Error('Server nicht erreichbar. Bitte versuche es erneut.');
      }

      const data = await res.json();
      if (data.error) throw new Error(data.error);

      if (data.type === 'proposal') {
        setEditMessages(prev => [...prev, { role: 'assistant', content: data.message, isProposal: true }]);
        setPendingProposal(data);
      } else {
        setEditMessages(prev => [...prev, { role: 'assistant', content: data.message }]);
      }
    } catch (err) {
      console.error('Task edit error:', err);
      setError(err.message);
    }
    setIsLoading(false);
  }, [summaryData, departmentName]);

  const handleSend = useCallback(async () => {
    const text = inputValue.trim();
    if (!text || isLoading) return;
    voiceRef.current?.stop();

    const userMessage = { role: 'user', content: text };
    const updatedMessages = [...editMessages, userMessage];
    setEditMessages(updatedMessages);
    setInputValue('');
    setPendingProposal(null);

    await sendToEditChat(updatedMessages);
  }, [inputValue, isLoading, editMessages, sendToEditChat]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  const handleApply = useCallback(() => {
    if (!pendingProposal) return;
    onApplyChanges(pendingProposal.updatedSummary);
    setEditMessages(prev => [...prev, { role: 'system', content: 'Änderung übernommen.' }]);
    setPendingProposal(null);
    setTimeout(() => { setEditMessages([]); setError(null); }, 2000);
  }, [pendingProposal, onApplyChanges]);

  const handleReject = useCallback(() => {
    if (!pendingProposal) return;
    const rejectMsg = { role: 'user', content: 'Nein, das passt nicht. Lass mich es anders erklären.' };
    const updatedMessages = [...editMessages, rejectMsg];
    setEditMessages(updatedMessages);
    setPendingProposal(null);
    sendToEditChat(updatedMessages);
  }, [pendingProposal, editMessages, sendToEditChat]);

  return (
    <div className="process-edit-panel">
      <div className="edit-panel-header">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3>Aufgabe anpassen</h3>
          {onClose && (
            <button className="panel-close" onClick={onClose} aria-label="Schließen">&times;</button>
          )}
        </div>
        <p className="edit-panel-hint">
          Beschreibe welche Änderungen du an deiner Aufgaben-Zusammenfassung vornehmen möchtest.
        </p>
      </div>

      <div className="edit-chat-messages">
        {editMessages.length === 0 && !isLoading && (
          <div className="edit-chat-welcome">
            <div className="welcome-icon">&#9998;</div>
            <p>Du kannst jetzt Änderungen an deiner Aufgabe beschreiben:</p>
            <ul className="welcome-examples">
              <li>&quot;Ändere die Priorität auf Geschwindigkeit&quot;</li>
              <li>&quot;Füge SharePoint als Datenquelle hinzu&quot;</li>
              <li>&quot;Das Ergebnis geht an die Projektleitung&quot;</li>
              <li>&quot;Ergänze einen Schmerzpunkt zur Abstimmung&quot;</li>
            </ul>
          </div>
        )}

        {editMessages.map((msg, i) => {
          if (msg.role === 'system') {
            return (
              <div key={i} className="edit-bubble system-confirm">
                <div className="edit-bubble-content">{msg.content}</div>
              </div>
            );
          }
          return (
            <div key={i} className={`edit-bubble ${msg.role} ${msg.isProposal ? 'proposal' : ''}`}>
              <div className="edit-bubble-content">{msg.content}</div>
            </div>
          );
        })}

        {pendingProposal && !isLoading && (
          <div className="proposal-actions">
            <button className="btn btn-apply" onClick={handleApply}>
              Übernehmen
            </button>
            <button className="btn btn-reject" onClick={handleReject}>
              Ablehnen
            </button>
          </div>
        )}

        {isLoading && (
          <div className="edit-bubble assistant loading-bubble">
            <div className="typing-indicator">
              <span /><span /><span />
            </div>
          </div>
        )}

        <div ref={chatEndRef} />
      </div>

      {error && (
        <div className="edit-chat-error">Fehler: {error}</div>
      )}

      <div className="edit-chat-input-area">
        <textarea
          ref={textareaRef}
          className="edit-chat-textarea"
          rows={3}
          placeholder="Beschreibe deine Änderung an der Aufgabe..."
          value={inputValue}
          onChange={e => { setInputValue(e.target.value); voiceRef.current?.markManualEdit(); }}
          onKeyDown={handleKeyDown}
          disabled={isLoading}
        />
        <div className="edit-input-footer">
          <VoiceInput ref={voiceRef} value={inputValue} setValue={setInputValue} />
          <button
            className="btn btn-edit-send"
            onClick={handleSend}
            disabled={!inputValue.trim() || isLoading}
            style={{ background: '#2E7C99', color: '#fff', borderColor: '#2E7C99' }}
          >
            Weiter
          </button>
        </div>
      </div>
    </div>
  );
}
